import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { validatePostalCode } from '@/lib/validators';

interface ParsedProperty {
  row: number;
  name: string;
  type: string;
  address: string;
  floor: string;
  postal_code: string;
  city: string;
  area_sqm: number | null;
  acquisition_cost: number | null;
  notes: string;
  errors: string[];
  valid: boolean;
}

const TEMPLATE_HEADERS = [
  'Référence',
  'Type (appartement/maison/bureaux/entrepot/local_commercial/parking/autre)',
  'Adresse',
  'Étage',
  'Code postal',
  'Ville',
  'Surface (m²)',
  'Coût acquisition (€)',
  'Notes',
];

export function ImportPropertiesDialog() {
  const [open, setOpen] = useState(false);
  const [selectedSciId, setSelectedSciId] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedProperty[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase.from('scis').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const downloadTemplate = () => {
    const csvContent = [
      TEMPLATE_HEADERS.join(','),
      'Apt 101,appartement,12 rue de la Paix,3ème,75001,Paris,45,150000,Vue sur cour',
      'Bureau A,bureaux,5 avenue des Champs,RDC,75008,Paris,120,500000,Proche métro',
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_biens.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const parseFrenchNumber = (value: string): number | null => {
    if (!value) return null;
    let cleaned = value.replace(/[€\s\u00a0]/g, '').trim();
    if (/,\d{1,2}$/.test(cleaned)) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  const parseCSV = (text: string): ParsedProperty[] => {
    const cleanText = text.replace(/^\ufeff/, '');
    const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    // Auto-detect delimiter from header line
    const headerLine = lines[0];
    const delimiter = (headerLine.split(';').length > headerLine.split(',').length) ? ';' : ',';

    const dataLines = lines.slice(1);
    
    return dataLines.map((line, index) => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const errors: string[] = [];
      const name = values[0] || '';
      const type = values[1] || '';
      const address = values[2] || '';
      const floor = values[3] || '';
      const postalCode = values[4] || '';
      const city = values[5] || '';
      const areaSqm = parseFrenchNumber(values[6] || '');
      const acquisitionCost = parseFrenchNumber(values[7] || '');
      const notes = values[8] || '';

      // Validation
      if (!address) {
        errors.push('Adresse requise');
      }
      if (postalCode) {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) {
          errors.push(postalValidation.message || 'Code postal invalide');
        }
      }
      if (areaSqm !== null && (isNaN(areaSqm) || areaSqm < 0)) {
        errors.push('Surface invalide');
      }
      if (acquisitionCost !== null && (isNaN(acquisitionCost) || acquisitionCost < 0)) {
        errors.push('Coût acquisition invalide');
      }

      return {
        row: index + 2,
        name,
        type,
        address,
        floor,
        postal_code: postalCode,
        city,
        area_sqm: areaSqm,
        acquisition_cost: acquisitionCost,
        notes,
        errors,
        valid: errors.length === 0,
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      setParsedData(parsed);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSciId) throw new Error('Veuillez sélectionner une SCI');
      
      const validProperties = parsedData.filter(p => p.valid);
      if (validProperties.length === 0) throw new Error('Aucun bien valide à importer');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const propertiesToInsert = validProperties.map(p => ({
        organization_id: profile.organization_id,
        sci_id: selectedSciId,
        name: p.name || null,
        type: p.type || null,
        address: p.address,
        floor: p.floor || null,
        postal_code: p.postal_code || null,
        city: p.city || null,
        area_sqm: p.area_sqm,
        acquisition_cost: p.acquisition_cost,
        notes: p.notes || null,
      }));

      const { error } = await supabase.from('properties').insert(propertiesToInsert);
      if (error) throw error;

      return validProperties.length;
    },
    onSuccess: (count) => {
      toast.success(`${count} bien(s) importé(s) avec succès`);
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      resetDialog();
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const resetDialog = () => {
    setOpen(false);
    setParsedData([]);
    setFileName('');
    setSelectedSciId('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validCount = parsedData.filter(p => p.valid).length;
  const invalidCount = parsedData.filter(p => !p.valid).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetDialog();
      else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Importer (CSV)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importer des biens immobiliers
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Template Download */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Télécharger le template</p>
                <p className="text-sm text-muted-foreground">
                  Utilisez ce fichier CSV comme modèle pour vos données
                </p>
              </div>
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Template CSV
              </Button>
            </div>
          </div>

          {/* SCI Selection */}
          <div className="space-y-2">
            <Label>SCI *</Label>
            <Select value={selectedSciId} onValueChange={setSelectedSciId}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une SCI" />
              </SelectTrigger>
              <SelectContent>
                {scis.map((sci) => (
                  <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Fichier CSV</Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="flex-1"
              />
            </div>
            {fileName && (
              <p className="text-sm text-muted-foreground">Fichier: {fileName}</p>
            )}
          </div>

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-4 mb-2">
                <Badge variant="secondary" className="gap-1">
                  <Check className="h-3 w-3" /> {validCount} valide(s)
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <X className="h-3 w-3" /> {invalidCount} erreur(s)
                  </Badge>
                )}
              </div>
              <ScrollArea className="flex-1 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Ligne</TableHead>
                      <TableHead className="w-16">Statut</TableHead>
                      <TableHead>Référence</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Ville</TableHead>
                      <TableHead>Erreurs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedData.map((property, idx) => (
                      <TableRow key={idx} className={!property.valid ? 'bg-destructive/10' : ''}>
                        <TableCell>{property.row}</TableCell>
                        <TableCell>
                          {property.valid ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                        <TableCell>{property.name || '-'}</TableCell>
                        <TableCell>{property.type || '-'}</TableCell>
                        <TableCell>{property.address}</TableCell>
                        <TableCell>{property.city || '-'}</TableCell>
                        <TableCell className="text-sm text-destructive">
                          {property.errors.join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={resetDialog}>
              Annuler
            </Button>
            <Button 
              onClick={() => importMutation.mutate()}
              disabled={validCount === 0 || !selectedSciId || importMutation.isPending}
            >
              {importMutation.isPending ? 'Import...' : `Importer ${validCount} bien(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}